import type { AnswerChoice } from "../../../skills/leadcue-reply/scripts/lib/answer-scope.ts";
import { type AppliesTo, choiceConsequence, reachLabel } from "../src/answer-scope-view.ts";

/**
 * "Applies to", the last thing on a question card before Save (docs/2026-09-27-question-card-answer-scope.md).
 *
 * Where the rep may choose, two options, each saying what it does. Where the host fixes the reach,
 * one line naming it and why. Both come from `appliesTo`, the same rule the server writes by.
 */
export function AppliesToField({
  rule,
  campaignName,
  choice,
  onChoice,
  name,
  heldIn = "standing_answers",
  campaignBlocked = null,
  disabled = false,
}: {
  rule: AppliesTo;
  campaignName: string | null;
  choice: AnswerChoice;
  onChoice: (next: AnswerChoice) => void;
  /** Unique per card, so two cards on one page never share a radio group. */
  name: string;
  /** Where the campaign half lands; the Yes/No card writes drafting context, not a Standing answer. */
  heldIn?: "standing_answers" | "campaign_context";
  /** Why "every later lead" cannot be chosen now (`campaignChoiceBlocked`); null when it can. */
  campaignBlocked?: string | null;
  disabled?: boolean;
}) {
  if (rule.kind === "fixed") return <AppliesToLine rule={rule} campaignName={campaignName} />;
  return (
    <fieldset className="lcr-applies" disabled={disabled}>
      <legend>Applies to</legend>
      {(["thread", "campaign"] as const).map((option) => {
        const blocked = option === "campaign" ? campaignBlocked : null;
        return (
          <label
            key={option}
            className="lcr-applies-option"
            data-blocked={blocked ? true : undefined}
          >
            <input
              type="radio"
              name={name}
              value={option}
              checked={choice === option}
              disabled={blocked !== null}
              aria-labelledby={`${name}-${option}-reach`}
              aria-describedby={`${name}-${option}-note`}
              onChange={() => onChoice(option)}
            />
            <span className="lcr-applies-text">
              {/* Named by its reach alone; what it does is the description, read after the name. */}
              <span className="lcr-applies-reach" id={`${name}-${option}-reach`}>
                {capitalised(reachLabel(option, campaignName))}
              </span>
              <span className="lcr-applies-note" id={`${name}-${option}-note`}>
                {blocked ?? choiceConsequence(option, heldIn)}
              </span>
            </span>
          </label>
        );
      })}
    </fieldset>
  );
}

/** A reach the rep cannot change, stated rather than hidden. */
export function AppliesToLine({
  rule,
  campaignName,
}: {
  rule: Extract<AppliesTo, { kind: "fixed" }>;
  campaignName: string | null;
}) {
  return (
    <p className="lcr-applies-fixed">
      Applies to <strong>{reachLabel(rule.reach, campaignName)}</strong>. {rule.reason}
    </p>
  );
}

function capitalised(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
